// 입고 CAPA 모니터링 → «17시 마감 기록» 탭 (옛 센터 일별 실적 «입고예정» 탭 · req-20261026-095719-d5a8b9 P2/4)
//   · 입고예정일 전날 17:00 마감 기준 예정 장 수·예정수량·예상 PLT·차량 수. 2.0 은 매일 17:05 자동 집계.
//   · 예상 PLT = 마감값 조건 줄만 입고 CAPA 와 같은 환산 — 추정이 섞였으면 노란 «추정 포함», 값이 없으면(계산 못 함·수기·옛 기록) '—'.
//   · 자동 고객사는 차량 수만 고친다(장 수·수량은 마감값이라 서버가 400). 1.0·미매핑은 장 수·수량도 수기.
//   · 센터 목록은 /api/inbound-capa/me 의 centers — 서버 응답 centers[].can_edit=false 센터는 입력을 막고 재집계 단추를 숨긴다
//     (실제 차단은 서버 403).
// 🔴 마감값이 없는 행은 0 이 아니라 '미집계'로 그린다 — 실제 0 과 구분해야 한다.
// 🔴 공유 문구는 서버 share_text 를 그대로 쓴다(화면·엑셀·메신저 숫자가 한 곳에서 나온다).
import { useState, useEffect, useCallback, useRef } from 'react';
import { inboundCapaAPI } from '../../api/client';
import { alertDialog, toast } from '../../components/AppDialog';
import { COLORS, badge as toneBadge } from '../../utils/uiStyles';
import { copyToClipboard } from '../../utils/clipboard';
import { fmtKstShort } from '../../utils/kstTime';

export const CLOSE_INTRO = '매일 17:00 에 익일 입고예정을 마감해 기록합니다. 17:30 판정은 [CAPA 모니터링] 탭에서 봅니다.';

const fmt = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('ko-KR'));
const num = (v) => (v === null || v === undefined ? '' : v);
const pair = (sheets, qty) => (sheets === null || sheets === undefined ? '—' : `${fmt(sheets)}장 / ${fmt(qty)}`);
const toValue = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

const card = { background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: '10px', padding: '14px', marginBottom: '12px' };
const th = { padding: '7px 9px', textAlign: 'left', fontSize: '12px', color: COLORS.textSub, whiteSpace: 'nowrap', borderBottom: `2px solid ${COLORS.border}` };
const td = { padding: '5px 9px', fontSize: '13px', borderBottom: `1px solid ${COLORS.borderSoft}`, whiteSpace: 'nowrap' };
const inp = { padding: '5px 7px', border: `1px solid ${COLORS.inputBorder}`, borderRadius: '6px', fontSize: '13px', width: '100%', boxSizing: 'border-box' };
const btn = (bg, color = '#fff') => ({ padding: '6px 12px', background: bg, color, border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 });
const note = (bg, color) => ({ ...card, background: bg, color, fontSize: '12px', padding: '10px 14px' });
const chip = (bg, color) => ({ display: 'inline-block', marginLeft: '4px', padding: '1px 6px', borderRadius: '999px', fontSize: '10px', background: bg, color });
const estBadge = { ...toneBadge('yellow'), marginLeft: '4px', fontSize: '10px' };

const HEADERS = ['화주', '시스템', '장 수', '예정수량', '예상 PLT', '차량 수', '마감 후 추가', '요청 전', '차량번호 입력', '출처', '집계 시각'];

const SUMMARY = [
  ['vehicles', '차량 수', (t) => `${fmt(t.vehicles)}대`],
  ['qty', '예정수량', (t) => fmt(t.close_qty)],
  ['sheets', '예정 장 수', (t) => `${fmt(t.close_sheets)}장`],
  ['late', '마감 후 추가', (t) => `${fmt(t.late_sheets)}장 / ${fmt(t.late_qty)}`],
  ['pre', '요청 전(참고)', (t) => `${fmt(t.pre_sheets)}장 / ${fmt(t.pre_qty)}`],
];

function sourceLabel(it) {
  switch (it.status) {
    case 'manual': return `수기${it.updater_name ? ` · ${it.updater_name}` : ''}`;
    case 'auto': return it.close_by && it.close_by !== '자동집계' ? `수동 재집계 · ${it.close_by}` : '자동';
    case 'pending': return '미집계';
    case 'missing_input': return '미입력';
    case 'unmapped': return '미매핑';
    case 'duplicate': return '중복 — 첫 행에 반영';
    default: return '—';
  }
}

const sourceColor = (status) => (['pending', 'missing_input'].includes(status) ? COLORS.warn
  : status === 'unmapped' ? COLORS.dangerText : COLORS.textMuted);

// 예상 PLT 칸 — 값이 없으면 '—', 추정이 섞였으면 노란 «추정 포함»
function PltCell({ value, estimated, testId }) {
  return (
    <td style={td} data-testid={testId}>
      {fmt(value)}
      {value !== null && value !== undefined && estimated ? <span data-testid="close-plt-estimated" style={estBadge}>추정 포함</span> : null}
    </td>
  );
}

export default function CloseTab({ me }) {
  const centers = me?.centers || [];
  const [center, setCenter] = useState('');       // '' = 볼 수 있는 센터 전부
  const [date, setDate] = useState(null);         // null = 서버 기본 날짜(첫 응답의 default_date 로 채운다)
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});         // {'센터|고객사id': {sheets?, qty?, vehicles?}}
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  // 첫 응답으로 날짜를 채울 때 같은 내용을 한 번 더 부르지 않게 한다.
  // 🔴 '센터|날짜' 키로 건너뛴다 — 단순 플래그면 늦게 온 응답이 다음 실제 조회를 삼킬 수 있다.
  const skipKey = useRef(null);

  const load = useCallback(async () => {
    const key = `${center}|${date || ''}`;
    if (skipKey.current === key) { skipKey.current = null; return; }
    skipKey.current = null;
    try {
      const r = await inboundCapaAPI.close(center, date || '');
      setData(r.data);
      setEdits({});
      setDirty(false);
      const filled = r.data?.default_date || r.data?.date;
      if (date === null && filled) {
        skipKey.current = `${center}|${filled}`;
        setDate(filled);
      }
    } catch (e) {
      if (e.response?.status === 403) alertDialog('이 센터에 접근할 권한이 없습니다.');
      else alertDialog('17시 마감 기록을 불러오지 못했습니다: ' + (e.response?.data?.detail || e.message));
      setData(null);
    }
  }, [center, date]);

  useEffect(() => { load(); }, [load]);

  // 차량 수를 여러 줄 고쳐 놓고 창을 닫으면 다시 적어야 한다
  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  const edit = (centerKey, shipperId, field, value) => {
    const key = `${centerKey}|${shipperId}`;
    setEdits((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
    setDirty(true);
  };
  const shown = (centerKey, it, field, current) => {
    const e = edits[`${centerKey}|${it.shipper_id}`];
    return e && e[field] !== undefined ? e[field] : num(current);
  };

  const save = async () => {
    // 센터마다 한 번씩 순차 PUT — 보낸 칸만 바뀐다(차량 수를 비우면 기본값으로 되돌린다)
    const byCenter = {};
    Object.entries(edits).forEach(([key, fields]) => {
      const [centerKey, id] = key.split('|');
      const row = { shipper_id: Number(id) };
      Object.entries(fields).forEach(([f, v]) => { row[f] = toValue(v); });
      (byCenter[centerKey] = byCenter[centerKey] || []).push(row);
    });
    setBusy(true);
    try {
      for (const [centerKey, rows] of Object.entries(byCenter)) {
        await inboundCapaAPI.putClose({ center_key: centerKey, plan_date: data.date, rows });
      }
      toast('입고예정을 저장했습니다.');
      setDirty(false);
      load();
    } catch (e) { alertDialog('저장 실패: ' + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const resync = async () => {
    setBusy(true);
    try {
      const r = await inboundCapaAPI.resyncClose({ center_key: center, plan_date: data?.date || date });
      const d = r.data || {};
      toast(d.error ? `집계 실패: ${d.error}`
        : `마감값 ${d.filled || 0}곳 반영 · 기존 마감 유지 ${d.kept_closed || 0} · 수기 보존 ${d.kept_manual || 0}`
          + `${d.plt_failed ? ' · 예상 PLT 는 계산하지 못했습니다' : ''}${d.notice ? ` — ${d.notice}` : ''}`);
      load();
    } catch (e) { alertDialog('재집계 실패: ' + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  // 🔴 링크로 열면 토큰이 안 실려 인증 오류가 난다 → blob 으로 받아 저장(입고요청 엑셀과 같은 방식)
  const download = async () => {
    try {
      const r = await inboundCapaAPI.exportClose(center, data?.date || date || '');
      const name = center ? (centers.find((c) => c.center_key === center)?.display_name || center) : '전체';
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `입고예정_${name}_${data?.date || date}.xlsx`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) { alertDialog('내려받기에 실패했습니다: ' + (e.response?.data?.detail || e.message)); }
  };

  const copyShare = () => {
    copyToClipboard(data?.share_text || '')
      .then(() => toast('복사했습니다'))
      .catch(() => alertDialog('복사에 실패했습니다. 아래 문구를 직접 선택해 복사하세요.'));
  };

  const totals = data?.totals;
  const rules = data?.rules || {};
  // 재집계는 센터 하나를 골랐고 그 센터를 고칠 수 있을 때만(서버 응답 can_edit — 없으면 /me 값)
  const picked = center ? (data?.centers || []).find((c) => c.center_key === center) : null;
  const canResync = Boolean(center) && (picked ? picked.can_edit !== false
    : centers.find((c) => c.center_key === center)?.can_edit !== false);
  const anyEditable = (data?.centers || []).some((c) => c.can_edit !== false);

  return (
    <div data-testid="ic-close">
      <div data-testid="ic-close-intro" style={{ fontSize: '12px', color: COLORS.textSub, marginBottom: '10px' }}>{CLOSE_INTRO}</div>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
        <select aria-label="입고예정 센터" value={center} onChange={(e) => setCenter(e.target.value)} style={{ ...inp, width: 'auto', minWidth: '170px' }}>
          <option value="">볼 수 있는 전체 센터</option>
          {centers.map((c) => <option key={c.center_key} value={c.center_key}>{c.display_name}{c.temp_zone ? ` (${c.temp_zone})` : ''}</option>)}
        </select>
        <input type="date" aria-label="입고예정일" value={date || ''} onChange={(e) => setDate(e.target.value || null)} style={{ ...inp, width: 'auto' }} />
        <div style={{ flex: 1 }} />
        {canResync && <button type="button" onClick={resync} disabled={busy || !data} style={btn(COLORS.bgHover || '#f3f4f6', COLORS.text)}>↻ 재집계</button>}
        <button type="button" onClick={download} disabled={!data} style={btn(COLORS.successSoft, COLORS.successText)}>⬇ 엑셀 다운로드</button>
        <button type="button" onClick={copyShare} disabled={!data} style={btn(COLORS.primarySoft, COLORS.primaryDark)}>공유 문구 복사</button>
        <button type="button" onClick={save} disabled={busy || !dirty || !data || !anyEditable} style={btn(COLORS.primary)}>입고예정 저장</button>
      </div>

      {data && (
        <>
          <div style={{ fontSize: '12px', color: COLORS.textMuted, marginBottom: '10px' }}>
            입고예정일 {data.date} · 마감 {data.cutoff_at} KST(전날 {rules.cutoff} 까지 등록분) · 차량 수 기준: {rules.vehicle_rule_label}
          </div>

          {!data.close_due && (
            <div style={note(COLORS.infoSoft, COLORS.info)}>마감 집계 전 — {rules.close_run} KST 자동 집계</div>
          )}
          {totals?.pending_count > 0 && (
            <div style={note(COLORS.warnSoft, COLORS.warn)}>
              미집계 {totals.pending_count}곳 — 마감 집계가 아직 없거나 실패했습니다. 센터를 골라 «↻ 재집계»를 누르세요.
              (마감 이후 수정·삭제된 입고는 반영이 달라질 수 있습니다.)
            </div>
          )}
          {totals?.unmapped_count > 0 && (
            <div style={note(COLORS.dangerSoft, COLORS.dangerText)}>
              미매핑 {totals.unmapped_count}곳 — 화주코드가 없어 자동 집계가 안 됩니다. «센터 고객사 명부»에서 연결하거나 장 수·예정수량을 직접 입력하세요.
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', marginBottom: '12px' }}>
            {SUMMARY.map(([key, label, render]) => (
              <div key={key} style={{ ...card, marginBottom: 0 }}>
                <div style={{ fontSize: '11px', color: COLORS.textSub }}>{label}</div>
                <div data-testid={`inbound-sum-${key}`} style={{ fontSize: '18px', fontWeight: 700, marginTop: '4px' }}>{render(totals)}</div>
              </div>
            ))}
          </div>

          {data.centers.map((c) => {
            const readOnly = c.can_edit === false;
            return (
              <div key={c.center_key} data-testid="close-center" data-center-key={c.center_key} style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px', gap: '8px', flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: '14px' }}>
                    {c.display_name}{c.temp_zone ? ` (${c.temp_zone})` : ''}
                    {readOnly && <span data-testid="close-readonly" style={chip(COLORS.borderSoft, COLORS.textSub)}>보기 전용</span>}
                  </strong>
                  <span style={{ fontSize: '11px', color: COLORS.textMuted }}>
                    {c.ppv ? <span data-testid="close-ppv">차량 수 = 올림(예상 PLT ÷ 차량당 PLT {fmt(c.ppv)}) · </span> : null}
                    {!c.has_depot && '창고 미등록 — 전 고객사 수기 입력 · '}
                    {c.pending_count > 0 && <span style={{ color: COLORS.warn }}>미집계 {c.pending_count}곳 · </span>}
                    {c.manual_missing_count > 0 && <span style={{ color: COLORS.warn }}>미입력 {c.manual_missing_count}곳 · </span>}
                    {c.unmapped_count > 0 && <span style={{ color: COLORS.dangerText }}>미매핑 {c.unmapped_count}곳</span>}
                  </span>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1060px' }}>
                    <thead><tr>{HEADERS.map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
                    <tbody>
                      <tr data-testid="inbound-center-total" style={{ fontWeight: 700, background: COLORS.bg }}>
                        <td style={td}>센터 합계</td><td style={td} />
                        <td style={td}>{fmt(c.totals.close_sheets)}</td><td style={td}>{fmt(c.totals.close_qty)}</td>
                        <PltCell value={c.totals.close_plt} estimated={c.totals.close_plt_estimated} testId="close-plt-total" />
                        <td style={td}>{fmt(c.totals.vehicles)}</td>
                        <td style={td}>{pair(c.totals.late_sheets, c.totals.late_qty)}</td>
                        <td style={td}>{pair(c.totals.pre_sheets, c.totals.pre_qty)}</td>
                        <td style={td}>{fmt(c.totals.car_no_cnt)}</td><td style={td} /><td style={td} />
                      </tr>
                      {c.items.map((it) => (
                        <tr key={it.shipper_id} data-shipper-id={it.shipper_id} style={{ backgroundColor: it.status === 'unmapped' ? COLORS.dangerSoft : 'transparent' }}>
                          <td style={{ ...td, fontWeight: 600 }}>{it.display_name}{it.temp_zone ? <span style={{ fontSize: '11px', color: COLORS.textMuted }}> ({it.temp_zone})</span> : null}</td>
                          <td style={td}>{it.system_ver}</td>
                          {it.manual_input ? ['sheets', 'qty'].map((f) => (
                            <td key={f} style={{ ...td, width: '100px' }}>
                              <input type="number" min="0" data-field={f} aria-label={`${it.display_name} ${f === 'sheets' ? '장 수' : '예정수량'}`}
                                value={shown(c.center_key, it, f, f === 'sheets' ? it.close_sheets : it.close_qty)}
                                disabled={readOnly}
                                onChange={(e) => edit(c.center_key, it.shipper_id, f, e.target.value)} style={inp} />
                            </td>
                          )) : (
                            <>
                              <td style={td}>{fmt(it.close_sheets)}</td>
                              <td style={td}>{fmt(it.close_qty)}</td>
                            </>
                          )}
                          <PltCell value={it.close_plt} estimated={it.close_plt_estimated} testId="close-plt" />
                          <td style={{ ...td, width: '130px' }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <input type="number" min="0" data-field="vehicles" aria-label={`${it.display_name} 차량 수`}
                                value={shown(c.center_key, it, 'vehicles', it.vehicles)}
                                disabled={readOnly}
                                onChange={(e) => edit(c.center_key, it.shipper_id, 'vehicles', e.target.value)} style={{ ...inp, width: '64px' }} />
                              {it.vehicle_manual !== null && it.vehicle_manual !== undefined
                                ? <span style={chip(COLORS.primarySoft, COLORS.primaryDark)}>수기</span>
                                : it.vehicle_default !== null && it.vehicle_default !== undefined
                                  ? <span style={chip(COLORS.borderSoft, COLORS.textSub)}>기본</span> : null}
                            </div>
                          </td>
                          <td style={td}>{pair(it.late_sheets, it.late_qty)}</td>
                          <td style={td}>{pair(it.pre_sheets, it.pre_qty)}</td>
                          <td style={td}>{fmt(it.car_no_cnt)}</td>
                          <td style={{ ...td, fontSize: '11px', color: sourceColor(it.status) }}>{sourceLabel(it)}</td>
                          <td style={{ ...td, fontSize: '11px', color: COLORS.textMuted }}>
                            {it.close_at ? `마감 ${fmtKstShort(it.close_at)}` : ''}
                            {it.late_at ? `${it.close_at ? ' · ' : ''}추가 ${fmtKstShort(it.late_at)}` : ''}
                          </td>
                        </tr>
                      ))}
                      {!c.items.length && <tr><td style={{ ...td, color: COLORS.textMuted }} colSpan={HEADERS.length}>등록된 고객사가 없습니다. '센터 고객사 명부'에서 먼저 등록하세요.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
          {!data.centers.length && <div style={{ ...card, color: COLORS.textMuted, fontSize: '13px' }}>볼 수 있는 센터가 없습니다.</div>}

          <div style={card}>
            <div style={{ fontSize: '12px', color: COLORS.textSub, marginBottom: '6px' }}>공유 문구 미리보기</div>
            <div data-testid="inbound-share-text" style={{ fontSize: '13px', whiteSpace: 'pre-wrap' }}>{data.share_text}</div>
            <div style={{ fontSize: '11px', color: COLORS.textMuted, marginTop: '8px' }}>
              자동 집계 고객사는 차량 수만 고칠 수 있습니다. 차량 수를 비우고 저장하면 기본값으로 돌아갑니다.
              예상 PLT 는 마감에 든 입고만 입고 CAPA 와 같은 기준으로 환산합니다(«추정 포함» = SKU 적재 기준이 없는 상품이 섞임).
            </div>
          </div>
        </>
      )}
    </div>
  );
}
